import { CustomClaimValue, useAuth } from "@app/shared";
import { useCallback, useSyncExternalStore } from "react";
import { useHasClaim } from "./useHasClaim";

const STORAGE_KEY = "alerts.allOrganizations";

// A module-level store rather than useState or useStoredState, because the two things that have to
// agree on this switch are on opposite sides of the tree: the Alerts page renders DashboardLayout, and
// the top-nav bell lives inside it, so neither can be the other's ancestor and no context reaches
// both. useSyncExternalStore over one subscribable value means flipping it on the page re-renders the
// bell in the same commit, which is the whole point — a bell counting one organization above a table
// listing the fleet is worse than not offering the switch at all.
const listeners = new Set<() => void>();

const read = (): boolean => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
        // Private-mode / storage-disabled browsers: the switch still works, it just doesn't persist.
        return false;
    }
};

let current = read();

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    // Another tab flipping the switch writes localStorage but not this tab's `current`.
    const onStorage = (e: StorageEvent) => {
        if (e.key !== null && e.key !== STORAGE_KEY) return;
        current = read();
        listeners.forEach((l) => l());
    };
    window.addEventListener("storage", onStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener("storage", onStorage);
    };
};

const write = (value: boolean) => {
    if (current === value) return;
    current = value;
    try {
        window.localStorage.setItem(STORAGE_KEY, value ? "true" : "false");
    } catch {
        // Not persisting is survivable; failing to update the UI is not.
    }
    listeners.forEach((l) => l());
};

export type AlertOrganizationScope = {
    /** True when the user has asked to see every organization's alerts, and is allowed to. */
    allOrganizations: boolean;
    setAllOrganizations: (value: boolean) => void;
    /** Whether to offer the switch at all. Only a superadmin can read outside their current org. */
    canSeeAllOrganizations: boolean;
    /**
     * What to pass as AlertController's `organizationId`. Undefined means "don't narrow" — the
     * controller's base query is already the caller's full read scope, which for a superadmin is
     * every organization (BaseProvider.GetAll hands superadmins unsecured data).
     */
    organizationId: string | undefined;
    /** False while there is nothing to query yet: org-scoped but the current organization is unknown. */
    ready: boolean;
};

/**
 * The organization scope every alert read shares — the Alerts page's table and the top-nav bell's
 * badge. Alert visibility itself is enforced server-side by AlertProvider.GetSecuredQueryable; this
 * only decides how far to narrow within what the caller may already see, so a non-superadmin toggling
 * the stored flag by hand gains nothing.
 */
export function useAlertOrganizationScope(): AlertOrganizationScope {
    const { currentOrganizationId } = useAuth();
    const canSeeAllOrganizations = useHasClaim(CustomClaimValue.SuperAdmin);
    const stored = useSyncExternalStore(subscribe, () => current, () => false);

    const allOrganizations = canSeeAllOrganizations && stored;
    const setAllOrganizations = useCallback((value: boolean) => write(value), []);

    return {
        allOrganizations,
        setAllOrganizations,
        canSeeAllOrganizations,
        organizationId: allOrganizations ? undefined : currentOrganizationId,
        ready: allOrganizations || !!currentOrganizationId,
    };
}
